import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { listScheduledPage, getScheduledAttempts, getSubscription, getScheduledStateCounts } from "../api";
import SubscriptionDetailsModal from "../components/SubscriptionDetailsModal";
import DateRangeFilter, { presetRange } from "../components/DateRangeFilter";

const STATES = ["", "SCHEDULED", "IN_PROGRESS", "COMPLETED", "FAILED", "CANCELLED", "REVOKED"];

const PAGE_SIZE = 50;

const STATE_STYLE = {
  COMPLETED:                    { bg: "#dcfce7", color: "#166534" },
  SUCCESS:                      { bg: "#dcfce7", color: "#166534" },
  SCHEDULED:                    { bg: "#dbeafe", color: "#1e40af" },
  INITIATED:                    { bg: "#dbeafe", color: "#1e40af" },
  PENDING:                      { bg: "#dbeafe", color: "#1e40af" },
  IN_PROGRESS:                  { bg: "#fff7ed", color: "#c2410c" },
  NOTIFICATION_IN_PROGRESS:     { bg: "#fff7ed", color: "#c2410c" },
  FAILED:                       { bg: "#fee2e2", color: "#991b1b" },
  REVOKED:                      { bg: "#f1f5f9", color: "#475569" },
  CANCELLED:                    { bg: "#f1f5f9", color: "#475569" },
};

function Badge({ state }) {
  const style = STATE_STYLE[state] || { bg: "#f1f5f9", color: "#475569" };
  return <span style={{ ...styles.badge, backgroundColor: style.bg, color: style.color }}>{state}</span>;
}

function AttemptDots({ attempts, max = 3 }) {
  const n = Math.min(attempts || 0, max);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 8, height: 8, borderRadius: "50%",
            backgroundColor: i < n ? "#ef4444" : "#e2e8f0",
            display: "inline-block",
          }}
        />
      ))}
      <span style={{ marginLeft: 4, fontSize: 12, color: "#64748b" }}>{attempts}</span>
    </span>
  );
}

function fmtAmount(paise) {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function AttemptsModal({ appId, scheduled, onSubClick, onClose }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getScheduledAttempts(appId, scheduled.id)
      .then(setAttempts)
      .finally(() => setLoading(false));
  }, [appId, scheduled.id]);

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={m.box} onClick={(e) => e.stopPropagation()}>
        <div style={m.header}>
          <div>
            <h2 style={m.title}>Payment Attempts</h2>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
              Scheduled for {fmtDate(scheduled.payment_date)} &nbsp;·&nbsp; {fmtAmount(scheduled.amount)} &nbsp;·&nbsp; <Badge state={scheduled.state} />
            </div>
          </div>
          <button onClick={onClose} style={m.closeBtn}>✕</button>
        </div>

        <div style={m.metaRow}>
          <div style={m.meta}><span style={m.metaLabel}>Scheduled ID</span><code style={m.metaVal}>{scheduled.id}</code></div>
          <div style={m.meta}>
            <span style={m.metaLabel}>Subscription ID</span>
            <code style={{ ...m.metaVal, cursor: "pointer", textDecoration: "underline dotted", color: "#4f46e5" }} onClick={() => onSubClick(scheduled.subscription_id)}>
              {scheduled.subscription_id}
            </code>
          </div>
          <div style={m.meta}><span style={m.metaLabel}>Attempts</span><span style={m.metaVal}>{scheduled.attempts}</span></div>
        </div>

        <h3 style={m.subheading}>Attempts</h3>
        {loading ? (
          <div style={{ color: "#64748b", padding: 16 }}>Loading…</div>
        ) : attempts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 13 }}>No attempts yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {["Order ID", "Amount", "State", "PhonePe Txn ID", "Payment Due", "Created"].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attempts.map((a, i) => (
                  <tr key={a.id} style={i % 2 !== 0 ? { backgroundColor: "#fafafa" } : {}}>
                    {/* Shown in full: this is the merchantOrderId PhonePe support
                        asks for, so a truncated value is useless to copy. */}
                    <td style={styles.td}><code style={{ ...styles.mono, whiteSpace: "nowrap" }}>{a.id}</code></td>
                    <td style={styles.td}>{fmtAmount(a.amount)}</td>
                    <td style={styles.td}><Badge state={a.state} /></td>
                    <td style={styles.td}>{a.transaction_id ? <code style={{ ...styles.mono, whiteSpace: "nowrap" }}>{a.transaction_id}</code> : "—"}</td>
                    <td style={styles.td}>{fmtDate(a.payment_due_date)}</td>
                    <td style={styles.td}>{fmtDateTime(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AppTransactions() {
  const { app } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const [stateCounts, setStateCounts] = useState({});
  const [subIdInput, setSubIdInput] = useState("");
  const [subIdFilter, setSubIdFilter] = useState("");
  const [selectedScheduled, setSelectedScheduled] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [range, setRange] = useState(() => presetRange(0)); // default: today
  const { start: startDate, end: endDate } = range;

  const applySubId = useCallback(() => {
    setSubIdFilter(subIdInput.trim());
  }, [subIdInput]);

  const filterParams = useMemo(() => ({
    state: stateFilter || null,
    subscription_id: subIdFilter || null,
    start_date: startDate || null,
    end_date: endDate || null,
  }), [stateFilter, subIdFilter, startDate, endDate]);

  // Deliberately excludes `state`: the pill counts describe the whole filtered set,
  // so selecting one pill must not zero out the others.
  const countParams = useMemo(() => ({
    subscription_id: subIdFilter || null,
    start_date: startDate || null,
    end_date: endDate || null,
  }), [subIdFilter, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    getScheduledStateCounts(app.id, countParams)
      .then((c) => { if (!cancelled) setStateCounts(c); })
      .catch(() => { if (!cancelled) setStateCounts({}); });
    return () => { cancelled = true; };
  }, [app.id, countParams]);

  // `rows` is a single page now, so Export pulls the full filtered set on demand.
  const exportCsv = useCallback(async () => {
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Payment Date", "Subscription ID", "Transaction ID", "Amount", "State", "Attempts", "Created"];
    setExporting(true);
    let all = [];
    try {
      const BATCH = 1000;
      let batch, offset = 0;
      do {
        // Export walks every batch; none of them need the count.
        const res = await listScheduledPage(app.id, { ...filterParams, limit: BATCH, offset, with_count: false });
        batch = res.rows;
        all = all.concat(batch);
        offset += BATCH;
      } while (batch.length === BATCH);
    } finally {
      setExporting(false);
    }

    const csvRows = [
      headers.join(","),
      ...all.map((r) => [
        fmtDate(r.payment_date),
        r.subscription_id,
        r.transaction_id || r.merchant_order_id || "",
        (r.amount / 100).toFixed(2),
        r.state,
        r.attempts,
        fmtDateTime(r.created_at),
      ].map(escape).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions_${app.id}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, app.id]);

  const openSubscription = useCallback((subscriptionId) => {
    getSubscription(app.id, subscriptionId).then(setSelectedSub).catch(() => {});
  }, [app.id]);

  // A filter change invalidates the page number; kept separate from load() so
  // load can depend on `page` without re-triggering itself.
  useEffect(() => { setPage(0); }, [filterParams]);

  // Fetch only the displayed page instead of walking the whole result set.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The count only needs recomputing when the filtered set changes, and a
      // filter change always resets to page 0 — so ask for it there and reuse it
      // while paging. Saves the count(*) on every Prev/Next.
      const { rows: pageRows, total: count } = await listScheduledPage(app.id, {
        ...filterParams,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        with_count: page === 0,
      });
      setRows(pageRows);
      if (count !== null) {
        setTotal(count);
      } else if (page === 0) {
        // Header missing on a page-0 load: degrade to the page length so the
        // table still renders (the pager just stays hidden).
        setTotal(pageRows.length);
      }
    } finally {
      setLoading(false);
    }
  }, [app.id, filterParams, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h1 style={s.h1}>Transactions</h1>

      {/* State pills */}
      <div style={s.statePills}>
        {STATES.map((st) => {
          const style = STATE_STYLE[st] || { bg: "#f1f5f9", color: "#475569" };
          const active = stateFilter === st;
          const count = st === ""
            ? Object.values(stateCounts).reduce((a, b) => a + b, 0)
            : stateCounts[st];
          return (
            <button
              key={st}
              onClick={() => setStateFilter(st)}
              style={{
                ...s.pill,
                backgroundColor: active ? style.bg : "#f1f5f9",
                color: active ? style.color : "#64748b",
                border: active ? `1.5px solid ${style.color}` : "1.5px solid transparent",
                fontWeight: active ? 700 : 500,
              }}
            >
              {st || "All"}
              <span style={{ ...s.pillCount, color: active ? style.color : "#94a3b8" }}>
                {(count || 0).toLocaleString("en-IN")}
              </span>
            </button>
          );
        })}
      </div>

      {/* Date range — own row so the quick-select pills stay prominent */}
      <div style={s.dateFilters}>
        <DateRangeFilter value={range} onChange={setRange} showWindowLabel />
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <div style={s.searchRow}>
          <input
            value={subIdInput}
            onChange={(e) => setSubIdInput(e.target.value)}
            placeholder="Filter by Subscription ID"
            style={s.searchInput}
            onKeyDown={(e) => e.key === "Enter" && applySubId()}
          />
          <button onClick={applySubId} style={s.searchBtn}>Search</button>
          {subIdFilter && (
            <button onClick={() => { setSubIdFilter(""); setSubIdInput(""); }} style={s.clearBtn}>✕</button>
          )}
        </div>
        <span style={s.count}>{loading ? "…" : `${total.toLocaleString("en-IN")} results`}</span>
        {rows.length > 0 && (
          <button onClick={exportCsv} disabled={exporting} style={{ ...s.exportBtn, opacity: exporting ? 0.5 : 1, cursor: exporting ? "default" : "pointer" }}>{exporting ? "Exporting…" : "Export CSV"}</button>
        )}
      </div>

      {loading ? (
        <div style={s.loading}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={s.empty}>No transactions found.</div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Payment Date", "Subscription ID", "Transaction ID", "Amount", "State", "Attempts", "Created"].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.id}
                  style={{ backgroundColor: i % 2 !== 0 ? "#fafafa" : "#fff", cursor: "pointer" }}
                  onClick={() => setSelectedScheduled(row)}
                >
                  <td style={styles.td}>{fmtDate(row.payment_date)}</td>
                  <td style={styles.td}>
                    <code
                      style={{ ...styles.mono, color: "#4f46e5", textDecoration: "underline dotted", cursor: "pointer", whiteSpace: "nowrap" }}
                      title={row.subscription_id}
                      onClick={(e) => { e.stopPropagation(); openSubscription(row.subscription_id); }}
                    >
                      {row.subscription_id}
                    </code>
                  </td>
                  <td style={styles.td}>
                    {row.transaction_id ? (
                      <code style={{ ...styles.mono, whiteSpace: "nowrap" }} title={row.transaction_id}>{row.transaction_id}</code>
                    ) : row.merchant_order_id ? (
                      <code style={{ ...styles.mono, whiteSpace: "nowrap", color: "#94a3b8" }} title={`Merchant order ID (PhonePe txn ID not yet available): ${row.merchant_order_id}`}>{row.merchant_order_id}</code>
                    ) : "—"}
                  </td>
                  <td style={styles.td}>{fmtAmount(row.amount)}</td>
                  <td style={styles.td}><Badge state={row.state} /></td>
                  <td style={styles.td}><AttemptDots attempts={row.attempts} /></td>
                  <td style={styles.td}>{fmtDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div style={s.pagination}>
          <button onClick={() => setPage((p) => p - 1)} disabled={page === 0} style={{ ...s.pageBtn, opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={s.pageInfo}>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)} · {total.toLocaleString("en-IN")} total</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total} style={{ ...s.pageBtn, opacity: (page + 1) * PAGE_SIZE >= total ? 0.4 : 1 }}>Next →</button>
        </div>
      )}

      {selectedScheduled && (
        <AttemptsModal
          appId={app.id}
          scheduled={selectedScheduled}
          onSubClick={(subId) => { openSubscription(subId); }}
          onClose={() => setSelectedScheduled(null)}
        />
      )}
      {selectedSub && (
        <SubscriptionDetailsModal appId={app.id} subscription={selectedSub} onClose={() => setSelectedSub(null)} zIndex={1100} />
      )}
    </div>
  );
}

const s = {
  h1: { margin: "0 0 16px", fontSize: 22, color: "#1e293b" },
  statePills: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  pill: { padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", background: "none", transition: "all 0.15s" },
  pillCount: { marginLeft: 6, fontSize: 11, fontWeight: 700, opacity: 0.75 },
  dateFilters: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" },
  filters: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  searchRow: { display: "flex", gap: 6 },
  searchInput: { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, width: 280 },
  searchBtn: { padding: "8px 14px", backgroundColor: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  clearBtn: { padding: "8px 10px", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#64748b" },
  count: { marginLeft: "auto", fontSize: 13, color: "#94a3b8" },
  exportBtn: { padding: "8px 14px", backgroundColor: "#0f766e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  loading: { color: "#64748b", padding: 20 },
  empty: { textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 14 },
  pagination: { display: "flex", alignItems: "center", gap: 12, justifyContent: "center", marginTop: 16 },
  pageBtn: { padding: "7px 16px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, color: "#1e293b" },
  pageInfo: { fontSize: 13, color: "#64748b" },
};

const styles = {
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", fontSize: 13 },
  th: { padding: "10px 14px", textAlign: "left", backgroundColor: "#f8fafc", color: "#64748b", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" },
  td: { padding: "11px 14px", color: "#1e293b", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  mono: { fontSize: 11, backgroundColor: "#f1f5f9", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" },
};

const m = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  // Wide enough for the full 32-char order id and the PhonePe txn id side by side
  // without the attempts table needing a horizontal scroll.
  box: { backgroundColor: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 940, maxHeight: "90vh", overflowY: "auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  title: { margin: 0, fontSize: 18, color: "#1e293b" },
  closeBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#64748b", flexShrink: 0 },
  metaRow: { display: "flex", flexWrap: "wrap", gap: 20, marginBottom: 20, padding: "14px 16px", backgroundColor: "#f8fafc", borderRadius: 8 },
  meta: { display: "flex", flexDirection: "column", gap: 3 },
  metaLabel: { fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" },
  metaVal: { fontSize: 13, color: "#1e293b", wordBreak: "break-all" },
};
